// An Axios replay retains this key; an intentional new invocation gets a new key.
export function ensureWorkSubmissionKey(config, makeKey) {
  const method = String(config.method || "get").toLowerCase();
  if (method === "post" && /^\/?(?:jobs(?:\/|$)|apps\/|subscriptions\/(?:checkout|cancel|resume|downgrade-to-free)$|credits\/purchase$)/.test(config.url || "")) {
    config.headers = config.headers || {};
    if (!config.headers["Idempotency-Key"]) config.headers["Idempotency-Key"] = makeKey();
  }
  return config;
}

export function workResumeAction(resource) {
  if (resource && ['recovering', 'recovery_required', 'cancelling'].indexOf(resource.job_status || resource.status) !== -1) {
    return 'blocked';
  }
  if (resource && resource.job_id && resource.settlement_required) {
    return "settle";
  }
  return "run";
}

// settlement_pending is normally the brief step between "result stored" and
// "charge settled", which the server finishes by itself: keep polling, and only
// present it as stuck once it has lasted this long.
export const SETTLEMENT_GRACE_MS = 60000;

export function workPollingDelay(resource) {
  const state = resource && (resource.job_status || resource.status);
  if (state === 'recovery_required') return 30000;
  return ['complete','failed','cancelled','needs_credits'].indexOf(state) !== -1 ? null : 2500;
}

// An estimate is not a reservation. A shown free attempt must not become paid
// if another request takes the last unit before this submission reaches the API.
export function workFundingHeaders(estimate) {
  return { "X-Require-Allowance": estimate && estimate.trial ? "true" : "false" };
}

export function newWorkSubmissionKey() {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
}

function executable(config) {
  return String(config.method || "get").toLowerCase() === "post" &&
    /^\/?(?:jobs(?:\/|$)|apps\/)/.test(config.url || "");
}

async function bodyIdentity(data) {
  if (typeof FormData !== "undefined" && data instanceof FormData) {
    const entries = [];
    for (const [key, value] of data.entries()) {
      if (typeof value === "string") entries.push([key, value]);
      else entries.push([key, { name: value.name, type: value.type,
        digest: await sha256(await value.arrayBuffer()) }]);
    }
    return entries;
  }
  if (typeof data === "string") {
    try { return JSON.parse(data); } catch (err) { return data; }
  }
  return data || null;
}

async function sha256(value) {
  const bytes = typeof value === "string" ? new TextEncoder().encode(value) : value;
  const hash = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, "0")).join("");
}

function stable(value) {
  if (Array.isArray(value)) return value.map(stable);
  if (value && typeof value === "object") {
    const sorted = {};
    Object.keys(value).sort().forEach(key => { sorted[key] = stable(value[key]); });
    return sorted;
  }
  return value;
}

// Unknown outcomes survive later clicks and page reloads. Store only opaque
// hashes/keys, never files, credentials or request bodies. No expiry that could
// silently turn an unresolved invocation into a second paid operation.
export function createWorkSubmissionJournal(storage, makeKey = newWorkSubmissionKey) {
  return {
    async prepare(config, principal) {
      if (!executable(config)) return config;
      config.headers = config.headers || {};
      if (!config._workJournal) {
        const identity = JSON.stringify(stable([principal, config.url, await bodyIdentity(config.data)]));
        config._workJournal = "pending-work:" + await sha256(identity);
      }
      let saved = storage.getItem(config._workJournal);
      saved = saved ? JSON.parse(saved) : null;
      if (!saved) {
        saved = { key: config.headers["Idempotency-Key"] || makeKey(),
          allowance: config.headers["X-Require-Allowance"] };
        storage.setItem(config._workJournal, JSON.stringify(saved));
      }
      config.headers["Idempotency-Key"] = saved.key;
      if (saved.allowance !== undefined) config.headers["X-Require-Allowance"] = saved.allowance;
      return config;
    },
    finish(config, status) {
      if (!config || !config._workJournal) return;
      // Network failures and server errors retain the original operation.
      if (!status || status >= 500 || status === 408 || status === 429) return;
      const raw = storage.getItem(config._workJournal);
      if (raw && JSON.parse(raw).key === config.headers["Idempotency-Key"])
        storage.removeItem(config._workJournal);
    }
  };
}
